import { useState } from "react";
import { Button as MantineButton } from "@mantine/core";
// --- Internal Component Definitions to replicate Mantine UI without external dependencies ---

const Box = ({ children, p, w, style, ...props }) => (
    <div
        style={{ padding: p === "xs" ? "10px" : p, width: w, ...style }}
        {...props}
    >
        {children}
    </div>
);

const Group = ({ children, mb, align, wrap, pb, style, ...props }) => (
    <div
        style={{
            display: "flex",
            marginBottom: mb === "md" ? "16px" : 0,
            alignItems: align || "center",
            flexWrap: wrap || "wrap",
            paddingBottom: pb,
            gap: "10px",
            ...style,
        }}
        {...props}
    >
        {children}
    </div>
);

const Divider = ({ my }) => (
    <hr
        style={{
            margin: my === "sm" ? "12px 0" : "10px 0",
            border: 0,
            borderTop: "1px solid #dee2e6",
        }}
    />
);

const TextInput = ({ placeholder, value, onChange, style }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{
            boxSizing: "border-box",
            height: "36px",
            padding: "0 12px",
            borderRadius: "4px",
            border: "1px solid #ced4da",
            fontSize: "14px",
            fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            color: "#000",
            outline: "none",
            transition: "border-color 100ms ease",
            ...style,
        }}
        onFocus={(e) => (e.target.style.borderColor = "#228be6")}
        onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
    />
);

const Select = ({ placeholder, data, value, onChange, style }) => (
    <div style={{ position: "relative", ...style }}>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{
                boxSizing: "border-box",
                width: "100%",
                height: "36px",
                padding: "0 12px",
                paddingRight: "28px", // space for arrow
                borderRadius: "4px",
                border: "1px solid #ced4da",
                fontSize: "14px",
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                color: "#000",
                backgroundColor: "#fff",
                outline: "none",
                appearance: "none",
                cursor: "pointer",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#228be6")}
            onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
        >
            <option value="" disabled>
                {placeholder}
            </option>
            {data.map((opt) => (
                <option key={opt} value={opt}>
                    {opt}
                </option>
            ))}
        </select>
        {/* Custom Arrow */}
        <div
            style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "#868e96",
                fontSize: "10px",
            }}
        >
            ▼
        </div>
    </div>
);

const Button = ({
    children,
    onClick,
    variant,
    gradient,
    color,
    compact,
    radius,
    style,
}) => {
    let bg = "#e9ecef";
    let txt = "#000";
    let border = "1px solid transparent";

    if (variant === "gradient") {
        bg = `linear-gradient(${gradient.deg}deg, ${gradient.from}, ${gradient.to})`;
        txt = "#fff";
    } else if (variant === "outline" && color === "red") {
        bg = "transparent";
        txt = "#fa5252";
        border = "1px solid #fa5252";
    } else if (variant === "filled") {
        // Handle specific hex color passed by user or default blue
        bg = color && color.startsWith("#") ? color : "#228be6";
        txt = "#fff";
    } else if (variant === "default") {
        bg = "#f1f3f5";
        txt = "#495057";
        border = "1px solid #ced4da";
    }

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                cursor: "pointer",
                border,
                background: bg,
                color: txt,
                height: compact ? "26px" : "36px",
                padding: compact ? "0 12px" : "0 18px",
                fontSize: compact ? "12px" : "14px",
                borderRadius: radius === "xl" ? "32px" : "4px",
                fontWeight: 600,
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "opacity 0.2s ease",
                whiteSpace: "nowrap",
                flexShrink: 0, // Prevents button from collapsing in mobile scroll view
                ...style,
            }}
            onMouseOver={(e) => {
                if (variant !== "gradient" && variant !== "filled")
                    e.currentTarget.style.backgroundColor =
                        variant === "outline"
                            ? "rgba(250, 82, 82, 0.05)"
                            : "#e9ecef";
                if (variant === "gradient" || variant === "filled")
                    e.currentTarget.style.opacity = "0.9";
            }}
            onMouseOut={(e) => {
                if (variant !== "gradient" && variant !== "filled")
                    e.currentTarget.style.backgroundColor =
                        variant === "outline" ? "transparent" : bg;
                if (variant === "gradient" || variant === "filled")
                    e.currentTarget.style.opacity = "1";
            }}
        >
            {children}
        </button>
    );
};

const ScrollArea = ({ children, w }) => (
    <div
        style={{
            width: w,
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE
            WebkitOverflowScrolling: "touch",
        }}
    >
        <style>{`div::-webkit-scrollbar { display: none; }`}</style>
        {children}
    </div>
);

// --- End Internal Definitions ---

const SearchSortFilter = ({ onFilterChange }) => {
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("Recent");
    const [category, setCategory] = useState("");

    const handleFilterChange = () => {
        onFilterChange({ search, sort, category });
    };

    const handleClearFilters = () => {
        setSearch("");
        setSort("Recent");
        setCategory("");
        onFilterChange({ search: "", sort: "Recent", category: "" });
    };

    // Helper to update category and trigger filter immediately
    const handleCategorySelect = (newCategory) => {
        setCategory(newCategory);
        onFilterChange({ search, sort, category: newCategory });
    };

    const categories = [
        "Feature article",
        "Researcher Q & A",
        "Sports Nutritionist Q & A",
        "Olympic Experiences",
        "Food Factsheets",
        "Translated articles",
        "Conference reports",
        "Other",
    ];

    return (
        <>
            <Box p="xs" w="100%">
                {/* Top Row: Search, Sort, Actions */}
                <Group mb="md" align="center" style={{ width: "100%" }}>
                    <TextInput
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ flex: 1, minWidth: "200px" }}
                    />
                    <Select
                        placeholder="Sort"
                        data={["Recent", "Oldest"]}
                        value={sort}
                        onChange={setSort}
                        style={{ flex: 1, minWidth: "150px" }}
                    />
                    <Group style={{ flexShrink: 0 }}>
                        <MantineButton
                            onClick={handleFilterChange}
                            variant="gradient"
                            gradient={{ from: "cyan", to: "green", deg: 90 }}
                        >
                            Apply
                        </MantineButton>
                        <MantineButton
                            variant="outline"
                            color="red"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </MantineButton>
                    </Group>
                </Group>

                {/* Second Row: Horizontal Scrollable Category Bar */}
                <ScrollArea w="100%" type="scroll" offsetScrollbars={false}>
                    <Group wrap="nowrap" pb="5px">
                        <Button
                            compact
                            radius="xl"
                            variant={category === "" ? "filled" : "default"}
                            color={category === "" ? "#1bc455" : "gray"}
                            onClick={() => handleCategorySelect("")}
                        >
                            All
                        </Button>

                        {categories.map((cat) => (
                            <Button
                                key={cat}
                                compact
                                radius="xl"
                                variant={
                                    category === cat ? "filled" : "default"
                                }
                                color={category === cat ? "#1bc455" : "gray"}
                                onClick={() => handleCategorySelect(cat)}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                {cat}
                            </Button>
                        ))}
                    </Group>
                </ScrollArea>
            </Box>
            <Divider my="sm" />
        </>
    );
};

export default SearchSortFilter;
